import { Reducer } from 'redux';
import deepmerge from 'deepmerge';
import { ITag, IRecordedInterview, IRecordedInterviewProcessingStatus, RecordedInterviewSystemStatus } from 'types';
import {
  CoreActionTypes,
  OfferState,
  Status,
  VisaStatus,
  RecordedInterviewActionTypes,
  OfferUpdateActionTypes,
} from './types';
import { CoreActionTypes as RealCoreActionTypes } from '../core/types';
import { StudentActionTypes } from '../types';
import { DashboardActionTypes } from '../../dashboard/types';
import { createMultiIntakeTag } from '../../../utilities/tags/getAllTags';
import { DataActionTypes } from '../../data/types';
import { cloneDeep } from 'lodash';
import { IRecordedInterviewAssessmentSuccessPayload, UpdateCalendlyInterviewStatusPayload } from './actions';
import { toast } from 'react-toastify';

//This is a temporary fix and should be removed and line 4 uncommented once DTM-48 is fully merged in
enum IntakeStatus {
  FUTURE = 'Future',
  ACTIVE = 'Active',
  CLOSED = 'Closed',
  ARCHIVED = 'Archived',
}

const getStudentTags = (payload: {
  data: {
    tags: {
      [key: string]: {
        tags: [ITag];
      };
    };
  };
}) => {
  const studentNumbers = Object.keys(payload.data?.tags || {});
  if (studentNumbers[0]) {
    return payload.data.tags[studentNumbers[0]].tags;
  }
  return [];
};
// TODO: DTJ-264
// @ts-ignore
const initialState: OfferState = {
  offerId: '',
  intakeId: '',
  courses: [],
  // courseFeePaidToDate: 0,
  // accomFeePaidToDate: 0,
  status: Status.PRE_DEPOSIT,
  visaStatus: VisaStatus.NONE,
  intakeStatus: IntakeStatus.ARCHIVED,
};

const overwriteMerge = (_: [], sourceArray: []) => sourceArray;

/**
 * Calculates the recordedInterviewSystemStatus from a collection of interviews.
 * This mirrors the logic in shared-lib/src/helpers/utils/recorded-interviews/index.ts
 * The priority order determines which status "wins" when there are multiple interviews.
 */
const calculateRecordedInterviewSystemStatus = (
  recordedInterviews: IRecordedInterview | undefined,
): RecordedInterviewSystemStatus => {
  if (!recordedInterviews || Object.keys(recordedInterviews).length === 0) {
    return RecordedInterviewSystemStatus.NONE;
  }

  const statusesByPriority = [
    RecordedInterviewSystemStatus.INVITED,
    RecordedInterviewSystemStatus.RECORDED,
    RecordedInterviewSystemStatus.FAILED,
    RecordedInterviewSystemStatus.PASSED,
    RecordedInterviewSystemStatus.RESIT,
    RecordedInterviewSystemStatus.ATTEMPTED,
    RecordedInterviewSystemStatus.NONE,
  ];

  // Filter out DELETED and EXCEEDED statuses
  const filteredInterviews = Object.values(recordedInterviews).filter(
    interview =>
      interview.systemStatus !== RecordedInterviewSystemStatus.DELETED &&
      interview.systemStatus !== RecordedInterviewSystemStatus.EXCEEDED,
  );

  const status = statusesByPriority.find(statusByP =>
    filteredInterviews.some(interview => statusByP === interview.systemStatus),
  );

  return status ?? RecordedInterviewSystemStatus.NONE;
};

const reducer: Reducer<OfferState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_RECORD_SUCCESS:
      // if the student is in more than one intake we need to push on the multi intakes tag
      if (action.payload.student?.summary?.intakes && action.payload.student.summary.intakes.length > 1) {
        const newTag = createMultiIntakeTag(action.payload.student.summary);
        action.payload.student.offer.tags = [
          ...(action.payload.student.offer.tags ? action.payload.student.offer.tags : []),
          newTag,
        ];
      }
      return { ...initialState, ...action.payload.student.offer };
    case StudentActionTypes.UPDATE_STATUS_SUCCESS:
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_SUCCESS:
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_VISA_PREP_FORM_SUCCESS:
    case StudentActionTypes.UPDATE_CONFLICT_SUCCESS:
    case StudentActionTypes.UPDATE_OFFER_EVIDENCE_SUCCESS:
    case StudentActionTypes.DELETE_RECORDED_INTERVIEW_FILE_SUCCESS:
    case StudentActionTypes.SAVE_RECORDED_INTERVIEW_DOCUMENTS_SUCCESS:
    case RealCoreActionTypes.UPDATE_ASSIGNED_AGENTS_SUCCESS: {
      if (action.payload.offer) {
        return deepmerge(
          { ...state },
          { ...action.payload.offer },
          {
            arrayMerge: overwriteMerge,
          },
        );
      }
      return state;
    }
    case StudentActionTypes.TOGGLE_RECORDED_INTERVIEW_SUCCESS: {
      let { interviewHash, status, undo } = action.payload;

      if (interviewHash && status) {
        const interview = (state as any).recordedInterviews?.[interviewHash];

        const hasRecording = Object.values(interview.attempts).some(
          // @ts-ignore
          attempt => attempt.recorded === true,
        );

        if (undo && hasRecording) {
          status = RecordedInterviewSystemStatus.RECORDED;
        } else if (undo && !hasRecording) {
          status = RecordedInterviewSystemStatus.ATTEMPTED;
        }

        if (interview) {
          interview.systemStatus = status;
          const recordedInterviews = {
            ...((state as any).recordedInterviews ?? {}),
            [interviewHash]: interview,
          };
          // Recalculate status after toggle (pass/fail/resit)
          const recordedInterviewSystemStatus = calculateRecordedInterviewSystemStatus(recordedInterviews);
          return deepmerge(
            state,
            {
              recordedInterviews,
              recordedInterviewSystemStatus,
            },
            {
              arrayMerge: combineMerge,
            },
          );
        }
      }
      return state;
    }
    case StudentActionTypes.REQUEST_RECORDED_INTERVIEW_FAILURE: {
      toast.error(`Failed to request recorded interview(s): ${action.payload}`);
      return state;
    }
    case DashboardActionTypes.STUDENTS_TAGS_PIN_SUCCESS: {
      const tagIds = state.tags?.map(tag => tag.id) || [];
      const updatedTags = getStudentTags(action.payload);
      const tags: ITag[] = updatedTags.filter(tag => !tagIds.includes(tag.id));
      return {
        ...state,
        tags: [...(state.tags || []), ...tags],
      };
    }
    case DashboardActionTypes.STUDENTS_TAGS_UNPIN_SUCCESS: {
      if (action.payload.isStudentDetailSection) {
        const tagIds = action.payload?.tags.map((tag: ITag) => tag.id);
        const customTagsUpdated = state.tags?.filter(tag => !tagIds?.includes(tag.id)) || [];
        return {
          ...state,
          tags: [...customTagsUpdated],
        };
      }
      return state;
    }

    case CoreActionTypes.UPDATE_VISA_STATUS: {
      return {
        ...state,
        // SHLD-970
        // status: getUpdatedStudentStatus(action.payload, state.status),
        visaStatus: action.payload,
      };
    }
    case CoreActionTypes.UPDATE_OFFER_FIELD: {
      return {
        ...state,
        [action.payload.fieldName]: action.payload.value,
      };
    }
    case StudentActionTypes.UPDATE_VISA_STATUS_SUCCESS: {
      if (action.payload.offer) {
        return {
          ...action.payload.offer,
        };
      }
      return state;
    }
    case DataActionTypes.UPLOAD_FILE_SUCCESS:
    case StudentActionTypes.DELETE_FILE: {
      const { interviewHash, attemptHash, fileNames, isRecordedInterviewDocumentModal } = action.payload;
      const { interviewType } = action.payload;

      if (!isRecordedInterviewDocumentModal) {
        return state;
      }

      const newState = cloneDeep({ ...state });
      const recordedInterviews = newState[interviewType] as IRecordedInterview;
      recordedInterviews[interviewHash].attempts[attemptHash].fileNames = fileNames;

      return {
        ...newState,
      };
    }
    case StudentActionTypes.REQUEST_RECORDED_INTERVIEW_SUCCESS:
    case StudentActionTypes.UPDATE_RECORDED_INTERVIEW_DOC:
    case StudentActionTypes.CANCEL_RECORDED_INTERVIEW_SUCCESS: {
      const recordedInterviewsObj = action.payload.recordedInterviews;
      const recordedInterviews = {
        ...((state as any).recordedInterviews ?? {}),
        ...recordedInterviewsObj,
      };
      // Calculate the new status from the merged interviews
      const recordedInterviewSystemStatus = calculateRecordedInterviewSystemStatus(recordedInterviews);
      return deepmerge(
        state,
        {
          recordedInterviews,
          recordedInterviewSystemStatus,
        },
        {
          arrayMerge: combineMerge,
        },
      );
    }

    case RecordedInterviewActionTypes.RECORDED_INTERVIEW_ASSESSMENT_SUCCESS: {
      const { interviewHash, interview } = action.payload as IRecordedInterviewAssessmentSuccessPayload;
      const currentInterview = {
        ...(state as any).recordedInterviews?.[interviewHash],
      };
      delete interview.questions;
      const recordedInterviews = {
        ...((state as any).recordedInterviews ?? {}),
        [interviewHash]: {
          ...currentInterview,
          ...interview,
        },
      };
      // Recalculate status - assessment may change interview to PASSED/FAILED
      const recordedInterviewSystemStatus = calculateRecordedInterviewSystemStatus(recordedInterviews);
      return deepmerge(
        state,
        {
          recordedInterviews,
          recordedInterviewSystemStatus,
        },
        {
          arrayMerge: combineMerge,
        },
      );
    }
    case RecordedInterviewActionTypes.RECORDED_INTERVIEW_EDIT_QUESTION_SCORE_SUCCESS: {
      const { interviewHash, interview } = action.payload as IRecordedInterviewAssessmentSuccessPayload;
      const recordedInterviews = {
        ...((state as any).recordedInterviews ?? {}),
        [interviewHash]: interview,
      };
      // Recalculate status in case interview status changed
      const recordedInterviewSystemStatus = calculateRecordedInterviewSystemStatus(recordedInterviews);
      return deepmerge(
        state,
        {
          recordedInterviews,
          recordedInterviewSystemStatus,
        },
        {
          arrayMerge: combineMerge,
        },
      );
    }
    case RecordedInterviewActionTypes.REQUEST_VIDEO_PROCESSING_STATUS_SUCCESS: {
      const { processedVideos } = action.payload as IRecordedInterviewProcessingStatus;
      return {
        ...state,
        processedVideos: processedVideos ?? 0,
      };
    }
    case StudentActionTypes.GET_STUDENT_RECORDED_INTERVIEWS_SUCCESS: {
      if (action.payload.recordedInterviews) {
        return {
          ...state,
          recordedInterviews: action.payload.recordedInterviews,
        };
      }
      return state;
    }

    case OfferUpdateActionTypes.UPDATE_CALENDLY_INTERVIEW_INFO_STATUS: {
      const { infoStatus } = action.payload as UpdateCalendlyInterviewStatusPayload;
      return {
        ...state,
        calendlyInterviewsInfoStatus: infoStatus,
      };
    }
    default:
      return state;
  }
};

export { reducer as offerReducer };

const combineMerge = (target: Array<any>, source: Array<any>, options: any) => {
  const destination = target.slice();

  source.forEach((item, index) => {
    if (typeof destination[index] === 'undefined') {
      destination[index] = options.cloneUnlessOtherwiseSpecified(item, options);
    } else if (options.isMergeableObject(item)) {
      destination[index] = deepmerge(target[index], item, options);
    } else if (target.indexOf(item) === -1) {
      destination.push(item);
    }
  });
  return destination;
};
